import Handlebars from "handlebars";
import { Eye } from "lucide-react";
import { useMemo, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { secondaryButtonClasses } from "./TaxonomyCrudEditor";

/**
 * Isolated Handlebars environment mirroring what oo-user-service's
 * SendEmploymentContractHandler registers before compiling the template.
 */
const handlebars = Handlebars.create();
handlebars.registerHelper("ifEquals", function (this: unknown, arg1, arg2, options) {
	return arg1 == arg2 ? options.fn(this) : options.inverse(this);
});

/**
 * Example consultant matching the context the backend compiles the template
 * with ({ ...user, hourlyPay, holidayPay, hourlyPayTotal, organisation,
 * supplements }).
 */
const MOCK_CONSULTANT = {
	firstName: "Anna",
	lastName: "Andersson",
	email: "anna.andersson@example.com",
	profile: {
		socialSecurityNumber: "19900101-1234",
		phoneNumber: "070-123 45 67",
		address: { address1: "Storgatan 1", postalCode: "111 22", city: "Stockholm" },
		employment: {
			title: "Konsult",
			employmentOption: "STUDENT",
			collectiveAgreement: "KOMMUNAL",
		},
	},
	hourlyPay: 150,
	holidayPay: 12,
	hourlyPayTotal: "168",
	supplements: [
		{
			branch: "RESTAURANT",
			service: "CHEF",
			name: "Kock",
			supplement: 15,
			supplementInclHolidayPay: 16.8,
		},
	],
};

/** Fallbacks for {{organisation.*}} when the real value is missing. */
const MOCK_ORGANISATION = {
	name: "Organisationen AB",
	orgNo: "556000-0000",
	address: { address1: "Exempelgatan 1", postalCode: "111 22", city: "Stockholm" },
};

/** The `.content` CSS from oo-signing-service's pdf.handlebars wrapper. */
const PREVIEW_CSS = `
	html, body { margin: 0; padding: 0; }
	body { background-color: #fff; font-family: 'Open Sans', sans-serif; font-size: 9pt; font-weight: 300; }
	h2 { text-align: left; margin: 0 0 10pt 0; font-size: 12pt; font-weight: 600; }
	h3 { margin: 7pt 0; padding: 0; font-size: 10pt; font-weight: 500; }
	.content h2 { text-align: left; margin: 17pt 0 7pt 0; }
	.content h2:first-child { margin-top: 0; }
	.page { padding: 1cm 1.25cm; }
	.page + .page { border-top: 1px dashed #ccc; }
	.content section { margin: 17pt 0; }
	.content section:first-child { margin-top: 0; }
	.content label { display: block; font-weight: bold; margin: 3pt 0 1pt 0; }
	.content ul { margin: 8pt 0; padding: 0; }
	.content li { list-style-type: none; list-style-position: inside; margin: 0; padding: 0 0 5pt 14pt; position: relative; }
	.content li::before { content: '•'; font-size: 6pt; vertical-align: middle; position: absolute; left: 0; top: 0; line-height: 11pt; }
	.content strong { padding-right: 2pt; font-weight: 600; }
`;

interface EmploymentContractPreviewButtonProps {
	/** The handlebars template to preview (typically the unsaved editor value). */
	template: string;
	/**
	 * Values for {{organisation.*}} in the template. Pass the organisation —
	 * or, for a subsidiary template, the organisation overlaid with the
	 * subsidiary's name/orgNo/address, mirroring the backend's fall-through.
	 */
	organisation?: object;
	disabled?: boolean;
}

/**
 * "Förhandsgranska" button + dialog rendering an employment-contract template
 * compiled with mock consultant values, styled like the signed PDF. Reused by
 * the organisation Anställningsavtal leaf and the subsidiary form.
 */
export const EmploymentContractPreviewButton = ({
	template,
	organisation,
	disabled,
}: EmploymentContractPreviewButtonProps) => {
	const [open, setOpen] = useState(false);

	// Compile with mock consultant values + the provided organisation,
	// mirroring the backend's template context. Recomputed when opened.
	const preview = useMemo(() => {
		if (!open) return null;
		try {
			const content = handlebars.compile(template)({
				...MOCK_CONSULTANT,
				organisation: { ...MOCK_ORGANISATION, ...organisation },
			});
			return {
				html: `<!doctype html><html><head><meta charset="utf-8">
					<link rel="preconnect" href="https://fonts.googleapis.com">
					<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
					<link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;500;600&display=swap" rel="stylesheet">
					<style>${PREVIEW_CSS}</style></head>
					<body><div class="content">${content}</div></body></html>`,
			};
		} catch (err) {
			return { error: err instanceof Error ? err.message : String(err) };
		}
	}, [open, template, organisation]);

	return (
		<>
			<button
				type="button"
				onClick={() => setOpen(true)}
				disabled={disabled || !template.trim()}
				className={`${secondaryButtonClasses} inline-flex items-center gap-1.5`}
			>
				<Eye size={15} />
				Förhandsgranska
			</button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="flex h-[85vh] max-w-4xl flex-col">
					<DialogHeader>
						<DialogTitle>Förhandsgranskning</DialogTitle>
						<DialogDescription>
							Mallen ifylld med exempelvärden för en konsult — så här ser avtalet ut när det skickas
							för signering.
						</DialogDescription>
					</DialogHeader>
					{preview?.error ? (
						<div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
							Mallen kunde inte kompileras: {preview.error}
						</div>
					) : (
						<iframe
							title="Förhandsgranskning av anställningsavtal"
							sandbox=""
							srcDoc={preview?.html}
							className="min-h-0 w-full flex-1 rounded-md border border-gray-200 bg-white"
						/>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
};
